import React, { useState, useEffect, useMemo, useContext } from 'react'
import { Box, TextField, Button, Grid, InputLabel, InputAdornment, Divider, FormControl, 
  Select, MenuItem , Autocomplete, IconButton, FormLabel, RadioGroup,FormControlLabel, Chip
} from '@mui/material';
import { useForm, Controller } from "react-hook-form";
import { useSnackbar } from 'notistack';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DatePicker from '@mui/lab/DatePicker';
import DateAdapter from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MobileDateRangePicker from '@mui/lab/MobileDateRangePicker';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { format, parse, add, isValid } from 'date-fns';
import { AppContext } from 'core/context/';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import PersonIcon from '@mui/icons-material/Person';
import { getLibraryDetails } from 'services/property';
import { saveLibraryBooking } from 'services/bookings';
import { getBookingStatus } from 'utils/common';
import { getPassesByProperty } from 'services/passes';
import CardMembershipIcon from '@mui/icons-material/CardMembership';

const AddBooking = ({ bookingDetails = null, onBookingAdded, saveBtnText="Create" }) => {

  const { libraries } = useContext(AppContext);
  const defaultFormFields = useMemo(() => ({
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
    amount: '',
    amountPaid: '',
    paymentChannel: '',
    paymentDate: null,
    dueDate: null,
    passStartDate: null,
    libraryId: null,
    startTime: null,
    duration: null,
    passId: null,
  }),[]);

  const daysInPass = useMemo(() =>({
    'Day Pass': 1,
    'Week Pass': 7,
    'Month Pass': 30,
    'Quarter Pass': 90,
    'Year Pass': 365,
  }), []);

  const daysToNumberMapping = useMemo(() =>({sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6}),[]);

  const defaultTimeSlots = useMemo(() => ([
    {time: 0, label: '12:00 AM'}, {time: 1, label: '01:00 PM'}, {time: 2, label: '02:00 AM'}, {time: 3, label: '03:00 AM'},
    {time: 4, label: '04:00 AM'}, {time: 5, label: '05:00 AM'}, {time: 6, label: '06:00 AM'}, {time: 7, label: '07:00 AM'},
    {time: 8, label: '08:00 AM'}, {time: 9, label: '09:00 AM'}, {time: 10, label: '10:00 AM'}, {time: 11, label: '11:00 AM'},
    {time: 12, label: '12:00 PM'}, {time: 13, label: '01:00 PM'}, {time: 14, label: '02:00 PM'}, {time: 15, label: '03:00 PM'},
    {time: 16, label: '04:00 PM'}, {time: 17, label: '05:00 PM'}, {time: 18, label: '06:00 PM'}, {time: 19, label: '07:00 PM'},
    {time: 20, label: '08:00 PM'}, {time: 21, label: '09:00 PM'}, {time: 22, label: '10:00 PM'},{time: 23, label: '11:00 PM'}
  ]),[]);
  
  const defaultDurations = useMemo(() => ([
    {minutes: 60, label: '1 hr'},{minutes: 120, label: '2 hr'},{minutes: 180, label: '3 hr'},{minutes: 240, label: '4 hr'},{minutes: 300, label: '5 hr'},
  ]),[]);

  const defaultDaysEnabled = useMemo(() => ([0,1,2,3,4,5,6]), []);

  const { enqueueSnackbar } = useSnackbar();
  const [toggleDateRangePicker, setToggleDateRangePicker] = useState(false);
  const [librarySelected, setLibrarySelected] = useState(null);
  const [filterDates, setFilterDates] = React.useState([null, null]);
  const [startTime, setStartTime] = useState(null);
  const [duration, setDuration] = useState(null);
  const [libraryData, setLibraryData] = useState(null);
  const [daysEnabled, setDaysEnabled] = useState([...defaultDaysEnabled]);
  const [timeSlots, setTimeSlots] = useState([...defaultTimeSlots]);
  const [durations, setDurations] = useState([...defaultDurations]);
  const [bookingStatus, setBookingStatus] = useState('New');
  const [isEditable, setIsEditable] = useState(true);
  const [passes, setPasses] = useState([]);
  const [selectedPass, setSelectedPass] = useState({});

  const { handleSubmit, control, formState: { errors }, reset, setValue } = useForm({
    mode: 'onTouched',
    defaultValues: {...defaultFormFields}
  });
  // console.log('errors', errors)
  useEffect(() => {
    if(bookingDetails){
      const startTime =  bookingDetails?.fromTime?.split(":")[0];
      const data = {
        libraryId: bookingDetails?.library,
        firstName: bookingDetails?.user?.firstName,
        lastName: bookingDetails?.user?.lastName,
        mobile: bookingDetails?.user?.mobile,
        email: bookingDetails?.user?.email,
        amount: bookingDetails?.bookingTransaction?.amount || '',
        paymentChannel: (bookingDetails?.bookingTransaction?.paymentChannel),
        paymentDate: isValid(bookingDetails?.bookingTransaction?.paymentDate) ? parse(bookingDetails?.bookingTransaction?.paymentDate, 'dd-MM-yyyy', new Date()) : null,
        dueDate: isValid(bookingDetails?.bookingTransaction?.dueDate) ? parse(bookingDetails?.bookingTransaction?.dueDate, 'dd-MM-yyyy', new Date()) : null,
        startTime: Number(startTime),
        duration: bookingDetails?.duration,
        passId: bookingDetails?.passId,
        amountPaid: Number(bookingDetails?.bookingTransaction?.amount || 0) - Number(bookingDetails?.bookingTransaction?.amountDue || 0)
      }

      setLibrarySelected(bookingDetails?.libraryId);
      setFilterDates([parse(bookingDetails.fromDate, 'dd-MM-yyyy', new Date()), parse(bookingDetails.toDate, 'dd-MM-yyyy', new Date())]);
      setStartTime(Number(startTime));
      setDuration(Number(bookingDetails?.duration));
      if(bookingDetails?.passId){
        setSelectedPass({id: bookingDetails.passId})
        data['passStartDate'] = parse(bookingDetails.fromDate, 'dd-MM-yyyy', new Date());
      }
     
      reset({...data});
      const bookingStatus = getBookingStatus(bookingDetails);
      if(bookingStatus){
        setBookingStatus(bookingStatus);
      }
      setIsEditable((bookingDetails?.cancelled || false) === false && bookingStatus !== 'Completed');
    }
  }, [bookingDetails, reset]);

  useEffect(() => {
    if(librarySelected){
      getAllPasses(librarySelected);
    }
  }, [librarySelected]);

  function getAllPasses(propertyId){
    const propertyType='library';
    getPassesByProperty({propertyId, propertyType}).then(({response, error}) => {
      if(response){
        setPasses([...response]);
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    })
  }
  
  
  const getLibraryData = (libraryId ) => {

    if(libraryId){
      getLibraryDetails({libraryId}).then(({response, error}) => {
        if(response){
          setLibraryData({...response});
        } else {
          enqueueSnackbar((response?.message || error), {variant: 'error'});
          setLibraryData(null);
        }
      })
    }else{
      setLibraryData(null);
    }
  };

  const onLibraryChange = (val) => {
    if(val?.libraryId){
      setLibrarySelected(val.libraryId);
      setDuration(null);
      setStartTime(null);
      setFilterDates([null, null]);
      setSelectedPass({});
      setPasses([]);
      setValue('passStartDate', null);
    }else{
      resetBooingForm()
    }
  }

  useEffect(() => {
    if(librarySelected){
      getLibraryData(librarySelected);
    }
  }, [librarySelected])

  const resetBooingForm = () => {
    reset({...defaultFormFields});
    setLibrarySelected(null);
    setLibraryData(null);
    setDuration(null);
    setStartTime(null);
    setFilterDates([null, null]);
    setTimeSlots([...defaultTimeSlots]);
    setDurations([...defaultDurations]);
    setDaysEnabled([...defaultDaysEnabled]);
  };

  useEffect(() => {
    if(libraryData){
      setWorkingDays(libraryData);
      setWorkingHours(libraryData)
    }
  }, [libraryData]);

  
  function setWorkingDays(lib) {
    const workingDays = lib?.libraryDetails?.workingDays;
    const daysEnabled = Object.entries(workingDays).filter(([day, status]) => status === true).map(([day])=> daysToNumberMapping[day]);
    setDaysEnabled([...daysEnabled]);
  }

  // // Filter time slots from current time if date is today
  // function filterTodayTimSlots(filteredTimeSlots) {
  //   const [fromDate, toDate] = filterDates;
  //   if( (fromDate && !toDate && getDBDateFormat(fromDate) === getDBDateFormat(new Date())) || 
  //     (fromDate && toDate && getDBDateFormat(fromDate) === getDBDateFormat(new Date()) && getDBDateFormat(toDate) === getDBDateFormat(new Date())) 
  //   ){
  //     return filteredTimeSlots.filter(({time}) => time >= new Date().getHours()) 
  //   }
  //   return filteredTimeSlots;
  // }

  // Disbale time slots before current time if date is today
  function disableTodayTimSlots(time) {
    const [fromDate, toDate] = filterDates;
    if( (fromDate && !toDate && getDBDateFormat(fromDate) === getDBDateFormat(new Date())) || 
    (fromDate && toDate && getDBDateFormat(fromDate) === getDBDateFormat(new Date()) && getDBDateFormat(toDate) === getDBDateFormat(new Date())) 
    ){
      return time <= new Date().getHours();
    }
    return false;
  }

  function setWorkingHours(lib) {
    const {from = null, to = null} = lib?.libraryDetails?.workingHours;
    if(from && to) {
      const fromHr = from.split(':')[0];
      const toHr = to.split(':')[0];
      if(fromHr && toHr){
        let filteredTimeSlots = defaultTimeSlots.filter(({time}) => (time >= fromHr) && (time < toHr));
        // filteredTimeSlots = filterTodayTimSlots([...filteredTimeSlots]);
        setTimeSlots([...filteredTimeSlots])
      }
    }
  }

  const onSubmit = (data) => {
    const [fromDate, toDate] = filterDates;
    if(!fromDate || (fromDate === null && toDate === null)){
      enqueueSnackbar("Please select date", {variant: 'error'});
      return;
    }
    saveBookingData(formatPostData(data));
  }

  const formatPostData = (inputData) => {
    const data={};
    const [fromDate, toDate] = filterDates;
    const user = {
      ...(bookingDetails?.user ? bookingDetails.user : {}),
      firstName: inputData.firstName,
      lastName: inputData.lastName,
      mobile: inputData.mobile,
      email: inputData.email,
    };
    const bookingTransaction= {
      ...(bookingDetails?.bookingTransaction ? bookingDetails.bookingTransaction : {}),
      amount: parseFloat(Number(inputData.amount)).toFixed(2),
      paymentChannel: inputData.paymentChannel,
      paymentDate: getDBDateFormat(inputData.paymentDate),
      dueDate: getDBDateFormat(inputData.dueDate),
      amountDue: parseFloat(Number(inputData.amount) - Number(inputData.amountPaid)).toFixed(2)
    };

    data['libraryId'] = inputData?.libraryId?.libraryId;
    data['passId'] = selectedPass?.id;
    if(libraryData?.library?.hourlyBookingsEnabled) {
      data['fromTime'] = `${inputData.startTime.toString().padStart(2,'0')}:00`;
      data['toTime'] = `${(inputData.startTime + (inputData.duration/60)).toString().padStart(2,'0')}:00`;
      data['duration'] = inputData.duration;
    }else{
      data['fromTime'] = libraryData?.libraryDetails?.workingHours?.from;
      data['toTime'] = libraryData?.libraryDetails?.workingHours?.to;
      data['duration'] = (parseFloat(libraryData?.libraryDetails?.workingHours?.to) - parseFloat(libraryData?.libraryDetails?.workingHours?.from))*60;
    }
    data['fromDate'] = getDBDateFormat(fromDate);
    data['toDate'] = toDate ? getDBDateFormat(toDate) :  getDBDateFormat(fromDate);
    data['user'] = user;
    data['bookingTransaction'] = bookingTransaction;
    const finalData = {...bookingDetails, ...data}
    return finalData;
  }

  const saveBookingData = (data) => {
    if(bookingDetails?.cancelled === false || bookingStatus !== 'Completed'){
      saveLibraryBooking({
        ...data,
        ...(bookingDetails?.bookingId ? {bookingId: bookingDetails?.bookingId} : {})
      }).then(({response, error}) => {
        if(response){
          enqueueSnackbar((`Booking ${bookingDetails?.id ? 'updated' : 'added'} successfully`), {variant: 'success'});
          resetBooingForm();
          onBookingAdded();
        } else {
          enqueueSnackbar((response?.message || error), {variant: 'error'});
        }
      });
    }
  }

  const getFormattedDate = () => {
    const [fromDate, toDate] = filterDates || {};
    if((isValid(fromDate) && !toDate) || (isValid(fromDate) && isValid(toDate) && getDBDateFormat(fromDate) === getDBDateFormat(toDate))) {
      return `Date | ${format(fromDate,'dd MMM, yyyy')}`;
    } else if(isValid(fromDate) && isValid(toDate)  ){
      return `Date | ${format(fromDate,'dd MMM, yyyy')} - ${format(toDate,'dd MMM, yyyy')}`;
    } else {
      return 'Select Date';
    }
  }

  function onDateSelected(dateRange) {
    setFilterDates(dateRange);
    setStartTime(null);
    setDuration(null);
    setValue('startTime', null, { shouldValidate: true }); 
    setValue('duration', null, { shouldValidate: true });
  }

  const onTimeSelected = (value) => {
    setStartTime(value);
    setValue('startTime', value, { shouldValidate: true }); 
    setValue('duration', null, { shouldValidate: true }); 
    setDuration(null);
  }

  const onDurationSelected = (value) => {
    setDuration(value);
    setValue('duration', value, { shouldValidate: true }); 
  }

  function getDBDateFormat(date) {
    return isValid(date) ? format(date, 'dd-MM-yyyy') : date;
  }

  function disableUnAvailableDurarion(duration) {
    const {to = null} = libraryData?.libraryDetails?.workingHours || {};
    if(startTime && to) {
      const toHr = to.split(':')[0];
      return !((startTime+(duration/60)) <= toHr);
    }
    return false;
  }

  const onPasselected = (passData) => {
    setFilterDates([null, null]);
    setValue('passStartDate', null);
    if(selectedPass?.id === passData?.id){
      setSelectedPass({});
      setValue('amount', '');
    }else{
      setValue('amount', passData?.price);
      setSelectedPass({...passData});
    }
  }

  function getHolidayCountBetweenDates(startDate, endDate, holidays){
    var totalHolidays = 0;
    for (var i = startDate; i <= endDate; i.setDate(i.getDate()+1)){
        if (holidays.includes(i.getDay())) totalHolidays++;
    }
    return totalHolidays;
 }

  function onPassDateSelected(date) {

    const startDate = new Date(date.getTime());
    const tempStartDate = new Date(date.getTime());
    const tempDate = new Date(date.getTime());
    const { passType } = selectedPass;
    const noOfDayToAdd = daysInPass[passType];
    const dateAfterAddingDay = add(tempDate, {days: (noOfDayToAdd - 1)});
    const workingDays = libraryData?.libraryDetails?.workingDays;
    const holidays=[];
    Object.keys(workingDays).forEach(function(day){
      if(workingDays[day] === false){
        holidays.push(daysToNumberMapping[day]);
      }
    })
    const holidayCount = getHolidayCountBetweenDates(tempStartDate, dateAfterAddingDay, holidays);
    const endDate = add(dateAfterAddingDay, {days: holidayCount});
    setFilterDates([startDate, endDate])
  }

  return (
    <Box noValidate component="form"  onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }} >
      <Grid container columnSpacing={2} rowSpacing={2} justifyContent="center">
        <Grid container item xs={12} sm={12} md={11} lg={10} xl={10}>
          <Grid item container columnSpacing={2}>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6} sx={{mt: 2.5, paddingLeft: 0}}>
              <Controller
                control={control}
                name="libraryId"
                id="libraryId"
                rules={{ required: "Select Library Name" }}
                render={({field }) => (
                  <Autocomplete
                    disabled={!isEditable}
                    {...field}
                    options={libraries}
                    size="small"
                    name="libraryId"
                    fullWidth
                    getOptionLabel={(option) => option?.name || option}
                    isOptionEqualToValue={(option, value) => option?.libraryId === value?.libraryId}
                    onChange={(event, newValue) => {
                      field.onChange(newValue);
                      onLibraryChange(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        {...field} 
                        error={!!errors?.libraryId}  
                        required 
                        fullWidth
                        label="Library Name"
                        InputProps={{
                          ...field,
                          ...params.InputProps,
                          startAdornment: 
                            <InputAdornment position="start">
                              <IconButton
                                aria-label="Library Name"
                                edge="end"
                              >
                                <LocalLibraryIcon />
                              </IconButton>
                            </InputAdornment>
                        }}
                      />
                    )}
                  />
                )}
                onChange={([, data]) => data}
              />
            </Grid>

            {((selectedPass?.id || undefined) === undefined) ? (
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6} sx={{mt: 2.5, visibility: libraryData ? 'visible' : 'hidden'}}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <MobileDateRangePicker
                      className='date-range-with-no-edit-icon'
                      startText="From Date"
                      endText="To Date"
                      value={filterDates}
                      clearable={true}
                      minDate={new Date()}
                      shouldDisableDate={(date) => !(daysEnabled.includes(date.getDay()))} 
                      open={toggleDateRangePicker}
                      onClose={() => setToggleDateRangePicker(false)}
                      onChange={(newValue) => {
                        onDateSelected(newValue);
                      }}
                      // onAccept={(data) => onDateAccepted(data)}
                      renderInput={(startProps, endProps) => (null)}
                    />
                  </LocalizationProvider>
                  <Button variant="contained" disabled={!isEditable} startIcon={<DateRangeIcon />} onClick={()=> setToggleDateRangePicker(true)} sx={{textTransform: 'none'}}>
                    {getFormattedDate()}
                  </Button>
              </Grid>
            ): null}

            {librarySelected && passes?.length > 0 ? (
              <Grid  item xs={12} sm={12} md={12} lg={12} >
                <Divider sx={{mt:2, mb: 1}} />
                <FormLabel>
                  <CardMembershipIcon sx={{verticalAlign: 'sub', mr: 1, fontSize: '1.2rem'}} />Passes
                </FormLabel>
                <Grid container item xs={12} sm={12} md={12} lg={12}  sx={{px:3}}>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <FormControl >
                      <Controller
                        control={control}
                        name="passId"
                        id="passId"
                        render={({field}) => (
                          <RadioGroup
                            {...field}
                            sx={{p:2 }}
                            row
                            aria-labelledby="demo-controlled-radio-buttons-group"
                            name="passId"
                            value={selectedPass?.id || null}
                            // onChange={onDurationSelected}
                          >
                            {passes.map((pass, index) =>(
                              <FormControlLabel 
                                key={index}
                                // value={time} 
                                // checked={startTime === time}
                                control={
                                  <Chip
                                    sx={{m:.5 }}
                                    label={pass?.title}
                                    color={selectedPass?.id === pass?.id ? "primary" : "default"}
                                    clickable
                                    disabled={!isEditable}
                                    onClick={() => onPasselected(pass)}
                                  />
                                }
                                label="" 
                              />
                            ))}
                          </RadioGroup>
                        )}
                      />
                    </FormControl>
                  </Grid>

                  {selectedPass?.id ? (
                    <Grid item xs={12} sm={12} md={12} lg={6} xl={6} sx={{mt: 1.2}}>
                      <Controller
                        control={control}
                        name="passStartDate"
                        id="passStartDate"
                        required
                        rules={{ required: "Select pass date",
                        }}
                        render={({field}) => (
                          <LocalizationProvider dateAdapter={DateAdapter} >
                            <DatePicker
                              {...field}
                              disabled={!isEditable}
                              required
                              label="Pass Start Date"
                              minDate={new Date()}
                              // shouldDisableDate={(date) => !(daysEnabled.includes(date.getDay()))} 
                              inputFormat="dd/MM/yyyy"
                              name="passStartDate"
                              clearable={false}
                              value={field.value}
                              onChange={(newValue) => {
                                field.onChange(newValue);
                                onPassDateSelected(newValue);
                              }}
                              // onChange={field.onChange}
                              renderInput={(params) => (
                                <TextField 
                                  required
                                  value={field.value}
                                  {...params} 
                                  error={!!errors?.passStartDate}  
                                  // helperText={errors?.paymentDate?.message}
                                  size="small" 
                                  sx={{width: '100%'}} 
                                  onKeyDown={(e) =>{ e.preventDefault(); return false;}}
                                />
                              )}
                            />
                          </LocalizationProvider>
                        )}
                      />
                    </Grid>
                  ): null}

                </Grid>
              </Grid>
            ):null}

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12} sx={{visibility: libraryData ? 'visible' : 'hidden', mt: 2}}>
              {libraryData?.library?.hourlyBookingsEnabled && ((selectedPass?.id || undefined) === undefined) ? (
                <>
                  <Grid item xs={12} sm={12} md={12} lg={12} sx={{ mt: 2 }}>
                    <Divider sx={{mt:2, mb: 1.5}}/>
                    {/* <Typography gutterBottom variant="subtitle1" component="p" sx={{mt:1}}>
                      <AccessTimeIcon sx={{verticalAlign: 'sub', mr: 1, fontSize: '1.2rem'}} />Select Time
                    </Typography> */}
                    <FormControl error={!!errors.startTime}>
                      <FormLabel id="demo-controlled-radio-buttons-group">
                        <AccessTimeIcon sx={{verticalAlign: 'sub', mr: 1, fontSize: '1.2rem'}} /> Select Time
                      </FormLabel>
                      <Controller
                        control={control}
                        name="startTime"
                        id="startTime"
                        required
                        rules={{ required: "Select start time" }}
                        render={({field}) => (
                          <RadioGroup
                            {...field}
                            sx={{p:2 }}
                            row
                            aria-labelledby="demo-controlled-radio-buttons-group"
                            name="startTime"
                            value={startTime}
                            // onChange={onDurationSelected}
                          >
                            {timeSlots.map(({time, label}, index) =>(
                              <FormControlLabel 
                                key={index}
                                // value={time} 
                                // checked={startTime === time}
                                control={
                                  <Chip
                                    sx={{m:.5 }}
                                    label={label}
                                    color={Number(startTime) === Number(time) ? "primary" : "default"}
                                    clickable
                                    disabled={disableTodayTimSlots(Number(time)) || !isEditable}
                                    onClick={() => onTimeSelected(time)}
                                  />
                                }
                                label="" 
                              />
                            ))}
                          </RadioGroup>
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={12} md={12} lg={12} >
                    <Divider sx={{mt:2, mb: 1.5}}/>
                    <FormControl error={!!errors.duration}>
                      <FormLabel id="demo-controlled-radio-buttons-group">
                        <HourglassTopIcon sx={{verticalAlign: 'sub', mr: 1, fontSize: '1.2rem'}} /> Select Duration
                      </FormLabel>
                      <Controller
                        control={control}
                        name="duration"
                        id="duration"
                        required
                        rules={{ required: "Select start timduration" }}
                        render={({field}) => (
                          <RadioGroup
                            {...field}
                            sx={{p:2 }}
                            row
                            aria-labelledby="demo-controlled-radio-buttons-group"
                            name="duration"
                            value={duration}
                            // onChange={onDurationSelected}
                          >
                            {durations.map(({minutes, label}, index) =>(
                              <FormControlLabel 
                                key={index}
                                value={minutes} 
                                control={
                                  <Chip
                                    sx={{m:.5 }}
                                    label={label}
                                    color={Number(minutes) === Number(duration) ? "primary" : "default"}
                                    clickable 
                                    onClick={() => onDurationSelected(minutes)}
                                    disabled={disableUnAvailableDurarion(minutes) || !isEditable}
                                  />
                                }
                                label="" 
                              />
                            ))}
                          </RadioGroup>
                        )}
                      />
                    </FormControl>
                  </Grid>
                </>
              ): null}
            
              <Grid  item xs={12} sm={12} md={12} lg={12} >
                <Divider sx={{mt:2, mb: 1}} />
                <FormLabel>
                  <PersonIcon sx={{verticalAlign: 'sub', mr: 1, fontSize: '1.2rem'}} />Student Details
                </FormLabel>
                <Grid container item xs={12} sm={12} md={12} lg={12}  sx={{px:3}}>
                  <Grid  item xs={12} sm={12} md={12} lg={12}>
                    <Controller
                      control={control}
                      name="firstName"
                      id="firstName"
                      rules={{ required: "Enter first name" }}
                      render={({field}) => (
                        <TextField
                          {...field}
                          disabled={!isEditable}
                          margin="normal"
                          label="First Name"
                          required
                          error={!!errors?.firstName}
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid  item xs={12} sm={12} md={12} lg={12}>
                    <Controller
                      control={control}
                      name="lastName"
                      id="lastName"
                      rules={{ required: "Enter last name" }}
                      render={({field}) => (
                        <TextField
                        {...field}
                          disabled={!isEditable}
                          margin="normal"
                          label="Last Name"
                          required
                          error={!!errors?.lastName}
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <Controller
                      control={control}
                      name="mobile"
                      id="mobile"
                      rules={{ required: "Enter mobile number",
                      pattern: {
                        value: /^[0-9]{10}$/i,
                        message: "Enter a valid 10 digit registered mobile number",
                      },
                    }}
                    render={({field}) => (
                        <TextField
                          {...field}
                          disabled={!isEditable}
                          margin="normal"
                          label="Mobile"
                          required
                          error={!!errors?.mobile}
                          helperText={errors.mobile?.message}
                          size="small"
                          fullWidth
                          // InputProps={{
                          //   endAdornment: <InputAdornment position="start">%</InputAdornment>,
                          // }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <Controller
                      control={control}
                      name="email"
                      id="email"
                      rules={{ required: "Enter email",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                        message: "Enter a valid email address",
                      },
                    }}
                    render={({field}) => (
                        <TextField
                          {...field}
                          disabled={!isEditable}
                          margin="normal"
                          label="Email"
                          required
                          error={!!errors?.email}
                          helperText={errors.email?.message}
                          size="small"
                          fullWidth
                          // InputProps={{
                          //   endAdornment: <InputAdornment position="start">%</InputAdornment>,
                          // }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid  item xs={12} sm={12} md={12} lg={12} sx={{mt: 1}}>
                <Divider sx={{mt:2, mb: 1}} />
                <FormLabel>
                  <PersonIcon sx={{verticalAlign: 'sub', mr: 1, fontSize: '1.2rem'}} />Payment Details
                </FormLabel>
                <Grid container item xs={12} sm={12} md={12} lg={12} columnSpacing={2} sx={{px:3}}>
                  <Grid  item xs={12} sm={12} md={6} lg={6}>
                    <Controller
                      control={control}
                      name="amount"
                      id="amount"
                      type="number"
                      rules={{ required: "Enter Booking amount" }}
                      render={({field}) => (
                        <TextField
                          {...field}
                          disabled={!isEditable}
                          type="number"
                          margin="normal"
                          label="Booking Amount"
                          required
                          error={!!errors?.amount}
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  <Grid  item xs={12} sm={12} md={6} lg={6}>
                    <Controller
                      control={control}
                      name="amountPaid"
                      id="amountPaid"
                      type="number"
                      rules={{ required: "Enter amount paid" }}
                      render={({field}) => (
                        <TextField
                          {...field}
                          disabled={!isEditable}
                          type="number"
                          margin="normal"
                          label="Amount Paid"
                          required
                          error={!!errors?.amountPaid}
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12} md={12} lg={6} xl={6} sx={{ mt: 1.2 }}>
                    <Controller
                      control={control}
                      name="paymentChannel"
                      id="paymentChannel"
                      required
                      rules={{ required: "Select payment type" }}
                      render={({field}) => (
                        <FormControl {...field} required fullWidth error={!!errors?.paymentChannel}>
                          <InputLabel id="paymentChannel-label" size="small">Payment Type</InputLabel>
                          <Select
                            disabled={!isEditable}
                            labelId="paymentChannel-label"
                            id="paymentChannel-select-id"
                            label="Payment Type"
                            name="paymentChannel"
                            size="small"
                            value={field.value}
                            onChange={field.onChange}
                          >
                            <MenuItem value='Cash'>Cash</MenuItem>
                            <MenuItem value='Cheque'>Cheque</MenuItem>
                            <MenuItem value='NEFT/IMPS'>NEFT/IMPS</MenuItem>
                            <MenuItem value='UPI'>UPI</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12} md={12} lg={6} xl={6} sx={{mt: 1.2}}>
                    <Controller
                      control={control}
                      name="paymentDate"
                      id="paymentDate"
                      required
                      rules={{ required: "Select payment date",
                        // validate: (value) => value?.getTime() >= new Date().getTime()  || "Date should be greater than today's date",
                        // validate: (value) => value?.getTime() >= watch('validityStartDate').getTime()  || "Validity End Date should be greater than Validity Start Date ",
                      }}
                      render={({field}) => (
                        <LocalizationProvider dateAdapter={DateAdapter} >
                          <DatePicker
                            {...field}
                            disabled={!isEditable}
                            label="Payment Date"
                            inputFormat="dd/MM/yyyy"
                            name="paymentDate"
                            clearable={false}
                            minDate={new Date(new Date() - (5*86400000))}
                            maxDate={new Date()}
                            value={field.value}
                            onChange={field.onChange}
                            renderInput={(params) => (
                              <TextField 
                                value={field.value}
                                {...params} 
                                error={!!errors?.paymentDate}  
                                // helperText={errors?.paymentDate?.message}
                                size="small" 
                                sx={{width: '100%'}} 
                                onKeyDown={(e) =>{ e.preventDefault(); return false;}}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={12} md={12} lg={6} xl={6} sx={{mt: 1.2}}>
                    <Controller
                      control={control}
                      name="dueDate"
                      id="dueDate"
                      required
                      rules={{ required: "Select due date",
                        // validate: (value) => value?.getTime() >= new Date().getTime()  || "Date should be greater than today's date",
                        // validate: (value) => value?.getTime() >= watch('validityStartDate').getTime()  || "Validity End Date should be greater than Validity Start Date ",
                      }}
                      render={({field}) => (
                        <LocalizationProvider dateAdapter={DateAdapter} >
                          <DatePicker
                            {...field}
                            disabled={!isEditable}
                            required
                            label="Due Date"
                            inputFormat="dd/MM/yyyy"
                            name="dueDate"
                            clearable={false}
                            minDate={new Date()}
                            value={field.value}
                            onChange={field.onChange}
                            renderInput={(params) => (
                              <TextField 
                                required
                                value={field.value}
                                {...params} 
                                error={!!errors?.dueDate}  
                                // helperText={errors?.paymentDate?.message}
                                size="small" 
                                sx={{width: '100%'}} 
                                onKeyDown={(e) =>{ e.preventDefault(); return false;}}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </Grid>

                </Grid>
              </Grid>

              {isEditable ? (
                  <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{ mt: 3, mb: 2, textTransform: 'inherit' }}
                    startIcon={ bookingDetails ? <ModeEditIcon />: <ControlPointIcon />}
                    color="primary"
                  >
                    {bookingDetails ? 'Update' : saveBtnText}
                  </Button>
                </Grid>
              ): null}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
 
export default React.memo(AddBooking);
